import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, ChefHat } from 'lucide-react';
import { Recipe } from '@/types/recipe';
import RecipeThumbnail from '@/components/ui/recipe-thumbnail';

interface CompositionRecipeDisplayProps {
  recipes: Recipe[];
}

const CompositionRecipeDisplay: React.FC<CompositionRecipeDisplayProps> = ({ recipes }) => {
  if (recipes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <ChefHat className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
        <p className="text-sm">No recipes in this composition yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recipes.map((recipe) => (
        <Card key={recipe.id} className="p-3 bg-card border-border/50 ios-card-hover ios-button-feedback">
          <Link 
            to={`/recipe/${recipe.id}`}
            className="block"
          >
            <div className="flex items-start gap-3">
              {/* Recipe Thumbnail */}
              <div className="shrink-0">
                {recipe.user_image_url || recipe.storage_image_url || recipe.image_url || recipe.user_image_path || recipe.storage_image_path ? (
                  <RecipeThumbnail 
                    userImageUrl={recipe.user_image_url}
                    userImagePath={recipe.user_image_path}
                    storageImageUrl={recipe.storage_image_url}
                    storageImagePath={recipe.storage_image_path}
                    imageUrl={recipe.image_url}
                    recipeName={recipe.title}
                    size="lg"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-muted/40 flex items-center justify-center">
                    <ChefHat className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Recipe Content */}
              <div className="flex-1 min-w-0">
                <div className="space-y-2">
                  {/* Recipe Title */}
                  <h3 className="font-semibold text-base line-clamp-2 text-foreground leading-tight ios-card-title">
                    {recipe.title}
                  </h3>
                  
                  {/* Recipe Description */}
                  {recipe.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {recipe.description}
                    </p>
                  )}
                  
                  {/* Recipe Metadata */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {(recipe.prepTime || recipe.cookTime) && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>
                          {recipe.prepTime && recipe.cookTime 
                            ? `${parseInt(recipe.prepTime) + parseInt(recipe.cookTime)} min`
                            : recipe.prepTime || recipe.cookTime
                          }
                        </span>
                      </div>
                    )}
                    {recipe.servings && (
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{recipe.servings} servings</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <ChefHat className="h-3 w-3" />
                      <span>{recipe.ingredients?.length || 0} ingredients</span>
                    </div>
                  </div>
                  
                  {/* Recipe Tags */}
                  {recipe.tags && recipe.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {recipe.tags.slice(0, 3).map((tag, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="text-xs px-2 py-0.5"
                        >
                          {tag}
                        </Badge>
                      ))}
                      {recipe.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs px-2 py-0.5">
                          +{recipe.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </Card>
      ))}
    </div>
  );
};

export default CompositionRecipeDisplay;
